import { useState, useEffect, useRef } from 'react';
import { X, Download, Smartphone, Share, Check, Rocket } from 'lucide-react';
import { isPWAInstalled, getPlatform, canInstallPWA } from '../utils/pwa/usePWA';
import { getBeforeInstallPrompt, subscribeBeforeInstallPrompt, type BeforeInstallPromptEvent } from '../utils/pwa/beforeInstallPrompt';
import { toast } from '../utils/toast';
import { devLog, devError } from '../utils/logger';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const platform = getPlatform();
  const hasShownUiThisSessionRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    // Don't show if already installed
    if (isPWAInstalled()) {
      return;
    }

    const shouldShowUiNow = () => {
      if (isPWAInstalled()) return false;
      if (hasShownUiThisSessionRef.current) return false;
      if (sessionStorage.getItem('pwa-prompt-shown-this-session')) return false;
      const dismissCount = parseInt(localStorage.getItem('pwa-dismiss-count') || '0');
      if (dismissCount >= 3) return false;
      const dismissedTimestamp = localStorage.getItem('pwa-install-dismissed');
      if (dismissedTimestamp) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedTimestamp)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 30) return false;
      }
      return true;
    };

    // iOS/macOS: show after 3s. Android/desktop: show only when beforeinstallprompt has fired (so "Installer nå" is available) or after 15s fallback (manual instructions).
    const isAndroidOrDesktop = platform === 'android' || platform === 'desktop';

    // Always subscribe/capture install prompt events, even if we decide not to show the UI.
    // This avoids missing beforeinstallprompt due to React effect timing.
    const unsub = subscribeBeforeInstallPrompt((e) => {
      if (!mounted) return;
      setDeferredPrompt(e);
      // If installation completed (appinstalled), hide the prompt immediately.
      if (!e) {
        setShowPrompt(false);
        return;
      }
      if (!shouldShowUiNow()) return;
      if (!canInstallPWA()) return;
      // Show immediately when Chrome provides a native install prompt.
      setShowPrompt(true);
      sessionStorage.setItem('pwa-prompt-shown-this-session', 'true');
      hasShownUiThisSessionRef.current = true;
    });

    // Sync immediately in case the event fired before this component mounted.
    const cached = getBeforeInstallPrompt();
    setDeferredPrompt(cached);
    if (cached && shouldShowUiNow() && canInstallPWA()) {
      setShowPrompt(true);
      sessionStorage.setItem('pwa-prompt-shown-this-session', 'true');
      hasShownUiThisSessionRef.current = true;
    }

    const timerShort = setTimeout(() => {
      if (!mounted) return;
      if (!shouldShowUiNow()) return;
      if (!canInstallPWA()) return;
      if (isAndroidOrDesktop) return; // Wait for beforeinstallprompt or fallback
      setShowPrompt(true);
      sessionStorage.setItem('pwa-prompt-shown-this-session', 'true');
      hasShownUiThisSessionRef.current = true;
    }, 3000);

    const timerFallback = isAndroidOrDesktop
      ? setTimeout(() => {
          if (!mounted) return;
          if (!shouldShowUiNow()) return;
          if (!canInstallPWA()) return;
          setShowPrompt(true);
          sessionStorage.setItem('pwa-prompt-shown-this-session', 'true');
          hasShownUiThisSessionRef.current = true;
        }, 15000)
      : undefined;

    return () => {
      mounted = false;
      clearTimeout(timerShort);
      if (timerFallback !== undefined) clearTimeout(timerFallback);
      unsub();
    };
  }, [platform]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Chrome/Android with beforeinstallprompt event
      devLog('📲 Showing install prompt...');
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          devLog('✅ User accepted PWA install');
        } else {
          devLog('❌ User dismissed PWA install');
        }
        
        setDeferredPrompt(null);
        setShowPrompt(false);
      } catch (error) {
        devError('Install prompt error:', error);
      }
    } else {
      // Fallback - show browser-specific instructions
      devLog('⚠️ No install prompt available - showing instructions');
      toast.info('For å installere appen', 'Trykk på meny-knappen (⋮) i nettleseren og velg "Installer app" eller "Legg til på hjemskjerm"');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    // ✅ Increment dismiss count
    const dismissCount = parseInt(localStorage.getItem('pwa-dismiss-count') || '0');
    localStorage.setItem('pwa-dismiss-count', (dismissCount + 1).toString());
  };

  if (!showPrompt || dismissed || isPWAInstalled()) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 xl:left-auto xl:right-4 xl:w-96 z-[10000] animate-slide-up pb-safe">
      <div className="bg-white rounded-2xl shadow-2xl border border-purple-100 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Smartphone className="w-5 h-5" />
            <span className="font-semibold">Installer appen</span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Lukk"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-gray-700 text-sm mb-3 flex items-center gap-1.5">
            Få raskere tilgang, push-varsler og en bedre opplevelse med Talentfunn-appen!
            <Rocket className="w-4 h-4 text-purple-600" />
          </p>

          {/* iOS Instructions */}
          {platform === 'ios' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <p className="text-xs text-blue-900 font-medium mb-2">
                Slik installerer du på iOS:
              </p>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>Trykk på Del-knappen <Share className="w-3 h-3 inline" /> nederst</li>
                <li>Bla ned og velg "Legg til på Hjem-skjerm"</li>
                <li>Trykk "Legg til"</li>
              </ol>
            </div>
          )}

          {/* macOS Safari Instructions */}
          {platform === 'macos' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <p className="text-xs text-blue-900 font-medium mb-2">
                Slik installerer du på Mac (Safari):
              </p>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>Trykk på Del-knappen <Share className="w-3 h-3 inline" /> i adresselinjen</li>
                <li>Velg "Legg til i Dock"</li>
                <li>Trykk "Legg til"</li>
              </ol>
            </div>
          )}

          {/* Android/Desktop - Native Install Button OR Manual Instructions */}
          {(platform === 'android' || platform === 'desktop') && (
            <>
              {/* Only show the native install CTA when Chrome provides beforeinstallprompt. */}
              {deferredPrompt && (
                <button
                  onClick={handleInstallClick}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all active:scale-95"
                >
                  <Download className="w-5 h-5" />
                  <span>Installer nå</span>
                </button>
              )}

              {!deferredPrompt && (
                // Show manual instructions if native prompt not available
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mt-3 mb-2">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Download className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-purple-900 mb-2">
                        Slik installerer du:
                      </p>
                      <ol className="text-sm text-purple-800 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-purple-600 flex-shrink-0">1.</span>
                          <span>Trykk på meny-knappen <span className="inline-block bg-purple-200 px-1.5 py-0.5 rounded text-xs font-mono">⋮</span> i nettleseren</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-purple-600 flex-shrink-0">2.</span>
                          <span>Velg <span className="font-medium">"Installer app"</span> eller <span className="font-medium">"Legg til på hjemskjerm"</span></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-purple-600 flex-shrink-0">3.</span>
                          <span>Følg instruksjonene for å fullføre installasjonen</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Benefits list */}
          <div className="mt-3 space-y-1.5">
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Raskere lasting og bedre ytelse</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Push-varsler for nye meldinger</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Fungerer offline</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Ingen nedlasting fra app store</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-100">
          <button
            onClick={handleDismiss}
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Ikke nå
          </button>
        </div>
      </div>
    </div>
  );
}
